import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import api from '../utils/api';
import DialogModal from '../components/DialogModal';
import {
  PageHeader,
  DataTable,
  Modal,
  FormInput,
  FormSelect,
  JournalEntryLineItems,
  StatusBadge
} from '../components/finance';
import { CircleNotch, CheckCircle, XCircle, WarningCircle } from '@phosphor-icons/react';
import './JournalEntriesDetailPage.css';

const JournalEntriesDetailPage = ({ autoOpenCreate = false }) => {
  const [entries, setEntries] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [showNewEntryModal, setShowNewEntryModal] = useState(autoOpenCreate);
  const [selectedEntry, setSelectedEntry] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingEntry, setEditingEntry] = useState(null);
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize] = useState(50);
  const [canNextPage, setCanNextPage] = useState(true);
  const [dialogConfig, setDialogConfig] = useState({
    isOpen: false,
    title: '',
    message: '',
    type: 'info',
    primaryAction: null,
    secondaryAction: null
  });
  const [reversalInputs, setReversalInputs] = useState({ reason: '', notes: '' });
  const [reversalLoading, setReversalLoading] = useState(false);
  const [reversalError, setReversalError] = useState(null);
  const [editFormData, setEditFormData] = useState({
    entry_date: '',
    reference_type: 'manual',
    reference_id: '',
    description: '',
    line_items: []
  });
  const [formData, setFormData] = useState({
    reference_type: 'manual',
    reference_id: '',
    entry_date: new Date().toISOString().split('T')[0],
    description: '',
    line_items: [
      { gl_account_id: '', debit_amount: 0, credit_amount: 0, description: '' },
      { gl_account_id: '', debit_amount: 0, credit_amount: 0, description: '' },
    ],
  });

  const referenceTypeOptions = [
    { label: 'Manual', value: 'manual' },
    { label: 'Payroll', value: 'payroll' },
    { label: 'Fee', value: 'fee' },
    { label: 'Expense', value: 'expense' }
  ];

  // Helper function to check if entry is in draft status
  const isDraft = (entry) => {
    return entry?.posting_status && entry.posting_status.toLowerCase() === 'draft';
  };

  // Helper function to check if entry is in posted status
  const isPosted = (entry) => {
    return entry?.posting_status && entry.posting_status.toLowerCase() === 'posted';
  };

  const fetchAccounts = useCallback(async () => {
    try {
      const data = await api.get('/api/chart-of-accounts');
      setAccounts(Array.isArray(data) ? data : data.accounts || []);
    } catch (error) {
      console.error('Failed to fetch accounts');
    }
  }, []);

  const fetchEntries = useCallback(async (pageNum = 0) => {
    try {
      setLoading(true);
      const skip = pageNum * pageSize;
      const data = await api.get(`/api/journal-entries?skip=${skip}&limit=${pageSize}`);
      const entriesList = Array.isArray(data) ? data : data.entries || [];
      setEntries(entriesList);
      setCanNextPage(entriesList.length === pageSize);
      setCurrentPage(pageNum);
    } catch (error) {
      toast.error('Failed to fetch journal entries');
    } finally {
      setLoading(false);
    }
  }, [pageSize]);

  useEffect(() => {
    fetchEntries(0);
    fetchAccounts();
  }, [fetchEntries, fetchAccounts]);

  const handleAddLineItem = () => {
    setFormData({
      ...formData,
      line_items: [
        ...formData.line_items,
        { gl_account_id: '', debit_amount: 0, credit_amount: 0, description: '' },
      ],
    });
  };

  const handleRemoveLineItem = (index) => {
    setFormData({
      ...formData,
      line_items: formData.line_items.filter((_, i) => i !== index),
    });
  };

  const handleLineItemChange = (index, field, value) => {
    const newItems = [...formData.line_items];
    newItems[index] = { ...newItems[index], [field]: value };

    // Enforce mutual exclusivity: debit and credit cannot both have values
    if (field === 'debit_amount' && value > 0) {
      newItems[index].credit_amount = 0; // Clear credit when debit is set
    } else if (field === 'credit_amount' && value > 0) {
      newItems[index].debit_amount = 0; // Clear debit when credit is set
    }

    setFormData({ ...formData, line_items: newItems });
  };

  const calculateTotals = () => {
    const debits = formData.line_items.reduce((sum, item) => sum + (parseFloat(item.debit_amount) || 0), 0);
    const credits = formData.line_items.reduce((sum, item) => sum + (parseFloat(item.credit_amount) || 0), 0);
    return { debits, credits };
  };

  const handleCreateEntry = async (e) => {
    e.preventDefault();
    const { debits, credits } = calculateTotals();
    
    if (Math.abs(debits - credits) > 0.01) {
      toast.error('Debits must equal credits!');
      return;
    }

    setSubmitting(true);
    try {
      const validLineItems = formData.line_items.filter(item => item.gl_account_id);

      if (validLineItems.length < formData.line_items.length) {
        toast.error('Every line item needs an account selected — remove or fill in the incomplete row(s)');
        return;
      }
      if (validLineItems.length < 2) {
        toast.error('Please provide at least 2 line items with valid accounts');
        return;
      }

      const submissionData = {
        ...formData,
        line_items: validLineItems.map(item => ({
          gl_account_id: item.gl_account_id,
          debit_amount: item.debit_amount,
          credit_amount: item.credit_amount,
          description: item.description
        }))
      };

      await api.post('/api/journal-entries', submissionData);
      toast.success('Journal entry created successfully');
      setShowNewEntryModal(false);
      setFormData({
        reference_type: 'manual',
        reference_id: '',
        entry_date: new Date().toISOString().split('T')[0],
        description: '',
        line_items: [
          { gl_account_id: '', debit_amount: 0, credit_amount: 0, description: '' },
          { gl_account_id: '', debit_amount: 0, credit_amount: 0, description: '' },
        ],
      });
      fetchEntries(0);
    } catch (error) {
      toast.error(error.message || 'Failed to create entry');
    } finally {
      setSubmitting(false);
    }
  };

  const handlePostEntry = async (entryId) => {
    try {
      await api.post(`/api/journal-entries/${entryId}/post`, {});
      toast.success('Entry posted successfully');
      fetchEntries(0);
    } catch (error) {
      toast.error(error.message || 'Failed to post entry');
    }
  };

  const showDialog = (config) => {
    setDialogConfig({
      isOpen: true,
      ...config
    });
  };

  const closeDialog = () => {
    setDialogConfig(prev => ({
      ...prev,
      isOpen: false
    }));
    setReversalInputs({ reason: '', notes: '' });
    setReversalError(null);
    setReversalLoading(false);
  };

  const handleDeleteEntry = (entryId) => {
    showDialog({
      title: 'Delete Journal Entry',
      message: 'Are you sure you want to delete this journal entry? This action cannot be undone.',
      type: 'warning',
      primaryAction: {
        label: 'Delete',
        onClick: async () => {
          closeDialog();
          try {
            await api.delete(`/api/journal-entries/${entryId}`);
            toast.success('Journal entry deleted successfully');
            setSelectedEntry(null);
            fetchEntries(0);
          } catch (error) {
            toast.error(error.message || 'Failed to delete entry');
          }
        }
      },
      secondaryAction: {
        label: 'Cancel',
        onClick: closeDialog
      }
    });
  };

  const handleReverseEntry = (entryId) => {
    setReversalInputs({ reason: '', notes: '' });
    setReversalError(null);
    setReversalLoading(false);
    showDialog({
      title: 'Reverse Journal Entry',
      message: 'Please provide the reason for reversing this entry.',
      type: 'info',
      primaryAction: {
        label: 'Reverse',
        onClick: async () => {
          if (!reversalInputs.reason.trim()) {
            setReversalError('Reversal reason is required');
            return;
          }
          setReversalLoading(true);
          setReversalError(null);
          try {
            const response = await api.post(`/api/journal-entries/${entryId}/reverse`, {
              reversal_reason: reversalInputs.reason.trim(),
              reversal_notes: reversalInputs.notes ? reversalInputs.notes.trim() : ''
            });
            toast.success(`Entry reversed successfully. Reversal entry: ${response.reversal_entry_id}`);
            setSelectedEntry(null);
            fetchEntries(0);
            closeDialog();
          } catch (error) {
            setReversalError(error.message || 'Failed to reverse entry');
            toast.error(error.message || 'Failed to reverse entry');
          } finally {
            setReversalLoading(false);
          }
        }
      },
      secondaryAction: {
        label: 'Cancel',
        onClick: closeDialog
      }
    });
  };

  const handleEditClick = async (entry) => {
    try {
      // Fetch full entry with all line items from backend using entry ID
      const fullEntry = await api.get(`/api/journal-entries/${entry.id}?include_line_items=true`);
      
      setEditingEntry(fullEntry);
      setEditFormData({
        entry_date: fullEntry.entry_date ? fullEntry.entry_date.split('T')[0] : new Date().toISOString().split('T')[0],
        reference_type: fullEntry.reference_type || 'manual',
        reference_id: fullEntry.reference_id || '',
        description: fullEntry.description || '',
        // Retrieve all line items from backend for this specific entry
        line_items: fullEntry.line_items && Array.isArray(fullEntry.line_items) 
          ? JSON.parse(JSON.stringify(fullEntry.line_items)) 
          : []
      });
      setShowEditModal(true);
    } catch (error) {
      toast.error('Failed to load entry details for editing');
      console.error('Error loading entry:', error);
    }
  };

  const handleEditFormChange = (e) => {
    const { name, value } = e.target;
    setEditFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleEditLineItemChange = (index, field, value) => {
    const newItems = [...editFormData.line_items];
    newItems[index] = { ...newItems[index], [field]: value };

    // Enforce mutual exclusivity: debit and credit cannot both have values
    if (field === 'debit_amount' && value > 0) {
      newItems[index].credit_amount = 0;
    } else if (field === 'credit_amount' && value > 0) {
      newItems[index].debit_amount = 0;
    }

    setEditFormData({ ...editFormData, line_items: newItems });
  };

  const handleAddEditLineItem = () => {
    setEditFormData({
      ...editFormData,
      line_items: [
        ...editFormData.line_items,
        { gl_account_id: '', debit_amount: 0, credit_amount: 0, description: '' },
      ],
    });
  };

  const handleRemoveEditLineItem = (index) => {
    setEditFormData({
      ...editFormData,
      line_items: editFormData.line_items.filter((_, i) => i !== index),
    });
  };

  const calculateEditTotals = () => {
    const debits = editFormData.line_items.reduce((sum, item) => sum + (parseFloat(item.debit_amount) || 0), 0);
    const credits = editFormData.line_items.reduce((sum, item) => sum + (parseFloat(item.credit_amount) || 0), 0);
    return { debits, credits };
  };

  const handleSaveEdit = async () => {
    const { debits, credits } = calculateEditTotals();
    
    if (Math.abs(debits - credits) > 0.01) {
      toast.error('Debits must equal credits!');
      return;
    }

    try {
      const updateData = {
        entry_date: editFormData.entry_date,
        reference_type: editFormData.reference_type,
        reference_id: editFormData.reference_id,
        description: editFormData.description,
        line_items: editFormData.line_items.map(item => ({
          gl_account_id: item.gl_account_id,
          debit_amount: item.debit_amount,
          credit_amount: item.credit_amount,
          description: item.description
        }))
      };

      await api.put(`/api/journal-entries/${editingEntry.id}`, updateData);
      toast.success('Entry updated successfully');
      setShowEditModal(false);
      setEditingEntry(null);
      setEditFormData({ entry_date: '', reference_type: 'manual', reference_id: '', description: '', line_items: [] });
      fetchEntries(0);
    } catch (error) {
      toast.error(error.message || 'Failed to update entry');
    }
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const { debits, credits } = calculateTotals();

  // DataTable columns configuration
  const columns = [
    {
      key: 'entry_date',
      label: 'Date',
      render: (value, fullRow) => new Date(fullRow.entry_date).toLocaleDateString()
    },
    { 
      key: 'reference_type', 
      label: 'Reference',
      render: (value, fullRow) => <span style={{ textTransform: 'capitalize' }}>{String(fullRow.reference_type || '').replace(/_/g, ' ')}</span>
    },
    { 
      key: 'description', 
      label: 'Description',
      render: (value, fullRow) => <span>{String(fullRow.description || '')}</span>
    },
    {
      key: 'line_items',
      label: 'Amount',
      render: (value, fullRow) => {
        // Use total_debit if available, otherwise calculate from line_items
        const amount = fullRow.total_debit || (
          Array.isArray(fullRow.line_items) 
            ? Math.abs(fullRow.line_items.reduce((sum, item) => sum + (item.debit_amount || 0), 0))
            : 0
        );
        return `GHS ${amount.toLocaleString()}`;
      }
    },
    {
      key: 'posting_status',
      label: 'Status',
      render: (value, fullRow) => <StatusBadge status={String(fullRow.posting_status || 'draft').toUpperCase()} />
    }
  ];

  // DataTable actions configuration
  const tableActions = [
    {
      label: 'View',
      className: 'primary',
      onClick: (row) => setSelectedEntry(row)
    },
    {
      label: 'Edit',
      className: 'secondary',
      onClick: (row) => {
        // Only allow edit if entry is in DRAFT status
        if (isDraft(row)) {
          handleEditClick(row);
        }
      },
      // Show Edit button ONLY for DRAFT entries
      visible: (row) => isDraft(row)
    },
    {
      label: 'Post',
      className: 'success',
      onClick: (row) => {
        // Only allow post if entry is in DRAFT status
        if (isDraft(row)) {
          handlePostEntry(row.id);
        }
      },
      // Show Post button ONLY for DRAFT entries
      visible: (row) => isDraft(row)
    },
    {
      label: 'Delete',
      className: 'danger',
      onClick: (row) => {
        // Only allow delete if entry is in DRAFT status
        if (isDraft(row)) {
          handleDeleteEntry(row.id);
        }
      },
      // Show Delete button ONLY for DRAFT entries
      visible: (row) => isDraft(row)
    },
    {
      label: 'Reverse',
      className: 'warning',
      onClick: (row) => {
        // Only allow reverse if entry is in POSTED status
        if (isPosted(row)) {
          handleReverseEntry(row.id);
        }
      },
      // Show Reverse button ONLY for POSTED entries
      visible: (row) => isPosted(row)
    }
  ];

  return (
    <div className="journal-detail-page">
      <PageHeader
        title="Journal Entries"
        subtitle="Record and manage journal entries"
        actions={[
          {
            label: 'New Entry',
            className: 'primary',
            onClick: () => setShowNewEntryModal(true)
          }
        ]}
      />

      <DataTable
        columns={columns}
        data={entries}
        actions={tableActions}
        loading={loading}
        emptyMessage="No journal entries found"
      />

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '20px', padding: '12px', backgroundColor: '#f9f9f9', borderRadius: '4px' }}>
        <div style={{ fontSize: '14px', color: '#666' }}>
          Page {currentPage + 1} • Showing {entries.length} entries
        </div>
        <div style={{ display: 'flex', gap: '8px' }}>
          <button
            onClick={() => fetchEntries(currentPage - 1)}
            disabled={currentPage === 0 || loading}
            style={{
              padding: '8px 16px',
              backgroundColor: currentPage === 0 || loading ? '#ccc' : '#007bff',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: currentPage === 0 || loading ? 'not-allowed' : 'pointer',
              fontSize: '14px'
            }}
          >
            ← Previous
          </button>
          <button
            onClick={() => fetchEntries(currentPage + 1)}
            disabled={!canNextPage || loading}
            style={{
              padding: '8px 16px',
              backgroundColor: !canNextPage || loading ? '#ccc' : '#007bff',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: !canNextPage || loading ? 'not-allowed' : 'pointer',
              fontSize: '14px'
            }}
          >
            Next →
          </button>
        </div>
      </div>

      <Modal
        isOpen={showNewEntryModal}
        onClose={() => {
          setShowNewEntryModal(false);
          setFormData({
            reference_type: 'manual',
            reference_id: '',
            entry_date: new Date().toISOString().split('T')[0],
            description: '',
            line_items: [
              { gl_account_id: '', debit_amount: 0, credit_amount: 0, description: '' },
              { gl_account_id: '', debit_amount: 0, credit_amount: 0, description: '' },
            ],
          });
        }}
        title="Create Journal Entry"
        actions={[
          {
            label: submitting ? 'Creating...' : 'Create Entry',
            className: 'primary',
            onClick: handleCreateEntry,
            isLoading: submitting,
            disabled: Math.abs(debits - credits) > 0.01 || submitting
          },
          {
            label: 'Cancel',
            className: 'secondary',
            onClick: () => setShowNewEntryModal(false),
            disabled: submitting
          }
        ]}
      >
        <form onSubmit={handleCreateEntry}>
          <FormInput
            label="Entry Date"
            name="entry_date"
            type="date"
            value={formData.entry_date}
            onChange={handleFormChange}
            disabled={submitting}
            required
          />

          <FormSelect
            label="Reference Type"
            name="reference_type"
            value={formData.reference_type}
            onChange={handleFormChange}
            options={referenceTypeOptions}
            disabled={submitting}
          />

          <FormInput
            label="Description"
            name="description"
            type="text"
            value={formData.description}
            onChange={handleFormChange}
            placeholder="e.g., Monthly utilities payment"
            disabled={submitting}
          />

          <JournalEntryLineItems
            items={formData.line_items}
            accounts={accounts}
            onAddItem={handleAddLineItem}
            onRemoveItem={handleRemoveLineItem}
            onChangeItem={handleLineItemChange}
            disabled={submitting}
          />
        </form>
      </Modal>

      <Modal
        isOpen={!!selectedEntry}
        onClose={() => setSelectedEntry(null)}
        title="Journal Entry Details"
        actions={[
          ...(isDraft(selectedEntry) ? [{
            label: 'Delete',
            className: 'danger',
            onClick: () => handleDeleteEntry(selectedEntry.id)
          }] : []),
          ...(isPosted(selectedEntry) ? [{
            label: 'Reverse',
            className: 'warning',
            onClick: () => handleReverseEntry(selectedEntry.id)
          }] : []),
          {
            label: 'Close',
            className: 'secondary',
            onClick: () => setSelectedEntry(null)
          }
        ]}
      >
        {selectedEntry && (
          <div className="entry-details">
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '16px' }}>
              <div>
                <label style={{ fontWeight: 600, color: '#666' }}>Date</label>
                <p>{selectedEntry.entry_date ? new Date(selectedEntry.entry_date).toLocaleDateString() : 'N/A'}</p>
              </div>
              <div>
                <label style={{ fontWeight: 600, color: '#666' }}>Reference</label>
                <p style={{ textTransform: 'capitalize' }}>{String(selectedEntry.reference_type || 'N/A').replace(/_/g, ' ')}</p>
              </div>
              <div style={{ gridColumn: '1 / -1' }}>
                <label style={{ fontWeight: 600, color: '#666' }}>Description</label>
                <p>{selectedEntry.description || 'N/A'}</p>
              </div>
              <div>
                <label style={{ fontWeight: 600, color: '#666' }}>Status</label>
                <p><StatusBadge status={String(selectedEntry.posting_status || 'draft').toUpperCase()} /></p>
              </div>
            </div>

            <h4 style={{ marginTop: '20px', marginBottom: '12px', fontSize: '14px', fontWeight: 600 }}>
              Line Items
            </h4>
            <div style={{ overflowX: 'auto' }}>
              <table style={{
                width: '100%',
                borderCollapse: 'collapse',
                fontSize: '13px'
              }}>
                <thead style={{ background: '#e8e8e8', borderBottom: '1px solid #d0d0d0' }}>
                  <tr>
                    <th style={{ padding: '8px', textAlign: 'left', fontWeight: 600 }}>GL Account</th>
                    <th style={{ padding: '8px', textAlign: 'left', fontWeight: 600 }}>Debit</th>
                    <th style={{ padding: '8px', textAlign: 'left', fontWeight: 600 }}>Credit</th>
                  </tr>
                </thead>
                <tbody>
                  {selectedEntry.line_items && Array.isArray(selectedEntry.line_items) && selectedEntry.line_items.length > 0 ? (
                    selectedEntry.line_items.map((item, idx) => (
                      <tr key={idx} style={{ borderBottom: '1px solid #e0e0e0' }}>
                        <td style={{ padding: '8px' }}>{item.gl_account_id || item.account_code}</td>
                        <td style={{ padding: '8px' }}>
                          {item.debit_amount ? `GHS ${item.debit_amount.toLocaleString()}` : '-'}
                        </td>
                        <td style={{ padding: '8px' }}>
                          {item.credit_amount ? `GHS ${item.credit_amount.toLocaleString()}` : '-'}
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="3" style={{ padding: '8px', textAlign: 'center', color: '#999' }}>
                        <div style={{ paddingBottom: '8px' }}>Line items details not available</div>
                        <div style={{ fontSize: '12px', color: '#666' }}>
                          Total Debit: GHS {(selectedEntry.total_debit || 0).toLocaleString()} | 
                          Total Credit: GHS {(selectedEntry.total_credit || 0).toLocaleString()}
                        </div>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </Modal>

      <Modal
        isOpen={showEditModal}
        onClose={() => {
          setShowEditModal(false);
          setEditingEntry(null);
          setEditFormData({ entry_date: '', reference_type: 'manual', reference_id: '', description: '', line_items: [] });
        }}
        title="Edit Journal Entry"
        actions={[
          {
            label: 'Save Changes',
            className: 'primary',
            onClick: handleSaveEdit,
            disabled: Math.abs(calculateEditTotals().debits - calculateEditTotals().credits) > 0.01
          },
          {
            label: 'Cancel',
            className: 'secondary',
            onClick: () => {
              setShowEditModal(false);
              setEditingEntry(null);
              setEditFormData({ entry_date: '', reference_type: 'manual', reference_id: '', description: '', line_items: [] });
            }
          }
        ]}
      >
        {editingEntry && (
          <form onSubmit={(e) => { e.preventDefault(); handleSaveEdit(); }}>
            <FormInput
              label="Entry Date"
              name="entry_date"
              type="date"
              value={editFormData.entry_date}
              onChange={handleEditFormChange}
              required
            />

            <FormSelect
              label="Reference Type"
              name="reference_type"
              value={editFormData.reference_type}
              onChange={handleEditFormChange}
              options={referenceTypeOptions}
            />

            <FormInput
              label="Reference ID (Optional)"
              name="reference_id"
              type="text"
              value={editFormData.reference_id}
              onChange={handleEditFormChange}
              placeholder="e.g., PR-001, FEE-2026-001"
            />

            <FormInput
              label="Description"
              name="description"
              type="text"
              value={editFormData.description}
              onChange={handleEditFormChange}
              placeholder="e.g., Monthly utilities payment"
            />

            <div style={{ marginTop: '20px' }}>
              <h4 style={{ fontSize: '14px', fontWeight: 600, marginBottom: '12px' }}>Line Items</h4>
              <div style={{ overflowX: 'auto', marginBottom: '12px' }}>
                <table style={{
                  width: '100%',
                  borderCollapse: 'collapse',
                  fontSize: '13px',
                  border: '1px solid #e0e0e0'
                }}>
                  <thead style={{ background: '#f5f5f5', borderBottom: '1px solid #d0d0d0' }}>
                    <tr>
                      <th style={{ padding: '10px', textAlign: 'left', fontWeight: 600 }}>Account</th>
                      <th style={{ padding: '10px', textAlign: 'left', fontWeight: 600 }}>Debit (GHS)</th>
                      <th style={{ padding: '10px', textAlign: 'left', fontWeight: 600 }}>Credit (GHS)</th>
                      <th style={{ padding: '10px', textAlign: 'left', fontWeight: 600 }}>Description</th>
                      <th style={{ padding: '10px', textAlign: 'center', fontWeight: 600 }}>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {editFormData.line_items.map((item, idx) => (
                      <tr key={idx} style={{ borderBottom: '1px solid #e0e0e0' }}>
                        <td style={{ padding: '10px' }}>
                          <select
                            value={item.gl_account_id || ''}
                            onChange={(e) => handleEditLineItemChange(idx, 'gl_account_id', e.target.value)}
                            required
                            style={{ width: '100%', padding: '6px', borderRadius: '4px', border: '1px solid #ccc' }}
                          >
                            <option value="">Select Account</option>
                            {accounts.map(account => (
                              <option key={account.id} value={account.id}>
                                {account.account_code} - {account.account_name}
                              </option>
                            ))}
                          </select>
                        </td>
                        <td style={{ padding: '10px' }}>
                          <input
                            type="number"
                            step="0.01"
                            value={item.debit_amount || ''}
                            onChange={(e) => handleEditLineItemChange(idx, 'debit_amount', parseFloat(e.target.value) || 0)}
                            style={{ width: '100%', padding: '6px', borderRadius: '4px', border: '1px solid #ccc' }}
                          />
                        </td>
                        <td style={{ padding: '10px' }}>
                          <input
                            type="number"
                            step="0.01"
                            value={item.credit_amount || ''}
                            onChange={(e) => handleEditLineItemChange(idx, 'credit_amount', parseFloat(e.target.value) || 0)}
                            style={{ width: '100%', padding: '6px', borderRadius: '4px', border: '1px solid #ccc' }}
                          />
                        </td>
                        <td style={{ padding: '10px' }}>
                          <input
                            type="text"
                            value={item.description || ''}
                            onChange={(e) => handleEditLineItemChange(idx, 'description', e.target.value)}
                            placeholder="Description"
                            style={{ width: '100%', padding: '6px', borderRadius: '4px', border: '1px solid #ccc' }}
                          />
                        </td>
                        <td style={{ padding: '10px', textAlign: 'center' }}>
                          {editFormData.line_items.length > 2 && (
                            <button
                              type="button"
                              onClick={() => handleRemoveEditLineItem(idx)}
                              style={{
                                padding: '4px 8px',
                                background: '#ff6b6b',
                                color: 'white',
                                border: 'none',
                                borderRadius: '4px',
                                cursor: 'pointer',
                                fontSize: '12px'
                              }}
                            >
                              Remove
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '12px', marginBottom: '12px', fontSize: '13px' }}>
                <div style={{ padding: '10px', background: '#f9f9f9', borderRadius: '4px', border: '1px solid #e0e0e0' }}>
                  <span style={{ fontWeight: 600, color: '#666' }}>Total Debits:</span>
                  <p style={{ margin: '4px 0 0 0', fontSize: '14px', fontWeight: 500 }}>
                    GHS {calculateEditTotals().debits.toFixed(2)}
                  </p>
                </div>
                <div style={{ padding: '10px', background: '#f9f9f9', borderRadius: '4px', border: '1px solid #e0e0e0' }}>
                  <span style={{ fontWeight: 600, color: '#666' }}>Total Credits:</span>
                  <p style={{ margin: '4px 0 0 0', fontSize: '14px', fontWeight: 500 }}>
                    GHS {calculateEditTotals().credits.toFixed(2)}
                  </p>
                </div>
                <div style={{ 
                  padding: '10px', 
                  background: Math.abs(calculateEditTotals().debits - calculateEditTotals().credits) < 0.01 ? '#d4edda' : '#f8d7da',
                  borderRadius: '4px', 
                  border: '1px solid #e0e0e0',
                  textAlign: 'center'
                }}>
                  <span style={{ fontWeight: 600, color: Math.abs(calculateEditTotals().debits - calculateEditTotals().credits) < 0.01 ? '#155724' : '#721c24', display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                    {Math.abs(calculateEditTotals().debits - calculateEditTotals().credits) < 0.01
                      ? <><CheckCircle size={16} /> Balanced</>
                      : <><XCircle size={16} /> Unbalanced</>}
                  </span>
                </div>
              </div>

              <button
                type="button"
                onClick={handleAddEditLineItem}
                style={{
                  width: '100%',
                  padding: '10px',
                  background: '#007bff',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '13px',
                  fontWeight: 500
                }}
              >
                + Add Line Item
              </button>
            </div>
          </form>
        )}
      </Modal>

      <DialogModal
        isOpen={dialogConfig.isOpen}
        onClose={closeDialog}
        title={dialogConfig.title}
        message={dialogConfig.message}
        type={dialogConfig.type}
        primaryAction={{
          ...dialogConfig.primaryAction,
          isLoading: reversalLoading,
          disabled: !reversalInputs.reason.trim() || reversalLoading
        }}
        secondaryAction={{
          ...dialogConfig.secondaryAction,
          disabled: reversalLoading
        }}
      >
        {dialogConfig.title === 'Reverse Journal Entry' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', padding: '12px 0' }}>
            {reversalError && (
              <div style={{
                padding: '12px',
                background: '#fee',
                border: '1px solid #fcc',
                borderRadius: '6px',
                color: '#c00',
                fontSize: '13px'
              }}>
                <WarningCircle size={16} style={{ verticalAlign: 'middle', marginRight: '4px' }} /> {reversalError}
              </div>
            )}
            <div>
              <label style={{ fontSize: '13px', fontWeight: '600', color: '#374151', marginBottom: '4px', display: 'block' }}>
                Reversal Reason <span style={{ color: '#dc2626' }}>*</span>
              </label>
              <input
                type="text"
                placeholder="Enter reason for reversal... (Required)"
                value={reversalInputs.reason}
                onChange={(e) => {
                  setReversalInputs(prev => ({ ...prev, reason: e.target.value }));
                  setReversalError(null);
                }}
                disabled={reversalLoading}
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  border: reversalError && !reversalInputs.reason.trim() ? '2px solid #dc2626' : '1px solid #d1d5db',
                  borderRadius: '6px',
                  fontSize: '13px',
                  backgroundColor: reversalLoading ? '#f3f4f6' : 'white',
                  cursor: reversalLoading ? 'not-allowed' : 'text'
                }}
                autoFocus
              />
              <p style={{ fontSize: '12px', color: '#6b7280', margin: '4px 0 0 0' }}>
                Explain why this entry is being reversed
              </p>
            </div>
            <div>
              <label style={{ fontSize: '13px', fontWeight: '600', color: '#374151', marginBottom: '4px', display: 'block' }}>
                Additional Notes <span style={{ color: '#9ca3af' }}>(Optional)</span>
              </label>
              <textarea
                placeholder="Enter any additional notes or comments..."
                value={reversalInputs.notes}
                onChange={(e) => setReversalInputs(prev => ({ ...prev, notes: e.target.value }))}
                disabled={reversalLoading}
                style={{
                  width: '100%',
                  minHeight: '80px',
                  padding: '10px 12px',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  fontSize: '13px',
                  backgroundColor: reversalLoading ? '#f3f4f6' : 'white',
                  cursor: reversalLoading ? 'not-allowed' : 'text',
                  fontFamily: 'inherit',
                  resize: 'vertical'
                }}
              />
              <p style={{ fontSize: '12px', color: '#6b7280', margin: '4px 0 0 0' }}>
                {reversalInputs.notes.length}/500 characters
              </p>
            </div>
            <div style={{
              padding: '12px',
              background: '#f0f9ff',
              border: '1px solid #bfdbfe',
              borderRadius: '6px',
              fontSize: '12px',
              color: '#1e40af'
            }}>
              ℹ️ A new reversing entry will be created automatically. Both entries will be visible in the journal for audit purposes.
            </div>
          </div>
        )}
      </DialogModal>
    </div>
  );
};

export default JournalEntriesDetailPage;


